import React, { useState, forwardRef, useImperativeHandle } from "react";

const injectPlaceholderStyle = () => {
  if (document.getElementById("__whr-num-ph")) return;
  const s = document.createElement("style");
  s.id = "__whr-num-ph";
  s.textContent = `
    .whr-num-input::placeholder { color: rgba(200,190,255,0.28); font-weight:400; font-size:13px; }
    .whr-num-input::-webkit-inner-spin-button,
    .whr-num-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
    .whr-num-input { -moz-appearance: textfield; }
  `;
  document.head.appendChild(s);
};

const ErrMsg = ({ msg }) => (
  <span style={{ color: "#e74c3c", fontSize: "11px", fontWeight: "500", marginTop: "3px", display: "block" }}>
    {msg}
  </span>
);

const NumberInputField = ({
  value, onChange, label,
  min = 0, max = 999, step = 1,
  disabled = false, colors,
  required = false, submitted = false, errorMsg = "Required",
}) => {
  const [hoverMinus, setHoverMinus] = useState(false);
  const [hoverPlus,  setHoverPlus]  = useState(false);

  const numVal = value === "" || value === null || value === undefined ? 0 : Number(value);
  const clamp  = (v) => Math.min(max, Math.max(min, v));

  const decrement = () => { if (!disabled) onChange(clamp(numVal - step)); };
  const increment = () => { if (!disabled) onChange(clamp(numVal + step)); };

  // ── Strip ALL non-digit characters before parsing ──────────────────────────
  const handleInput = (e) => {
    if (disabled) return;
    const raw = e.target.value;
    if (raw === "") { onChange(""); return; }
    // Remove anything that isn't a plain digit (handles "23e", "1.5", "-3" etc.)
    const digitsOnly = raw.replace(/[^0-9]/g, "");
    if (digitsOnly === "") { onChange(""); return; }
    const parsed = parseInt(digitsOnly, 10);
    if (!isNaN(parsed)) onChange(clamp(parsed));
  };

  // ── Block forbidden keys at keyboard level ─────────────────────────────────
  const handleKeyDown = (e) => {
    const blocked = [".", ",", "e", "E", "+", "-"];
    if (blocked.includes(e.key)) e.preventDefault();
  };

  const canDec    = !disabled && numVal > min;
  const canInc    = !disabled && numVal < max;
  const isInvalid = required && submitted && (
    value === "" || value === null || value === undefined ||
    value === 0  || value === "0"
  );

  const btnStyle = (hovered, canAct) => ({
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "38px", height: "42px",
    background: hovered && canAct ? colors.rowBg : "transparent",
    border: "none",
    color: hovered && canAct ? colors.text : colors.subtext,
    fontSize: "18px", fontWeight: "300",
    cursor: canAct ? "pointer" : "not-allowed",
    outline: "none", padding: 0,
    transition: "background 0.15s, color 0.15s", flexShrink: 0,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      <div style={{
        display: "inline-flex", alignItems: "center",
        backgroundColor: colors.inputBg,
        border: `1px solid ${isInvalid ? "#e74c3c" : colors.inputBorder}`,
        borderRadius: "8px", overflow: "hidden",
        height: "42px", userSelect: "none",
        opacity: disabled ? 0.45 : 1, flexShrink: 0,
        boxShadow: isInvalid ? "0 0 0 2px rgba(231,76,60,0.18)" : "none",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}>
        <button type="button" style={btnStyle(hoverMinus, canDec)}
          onClick={decrement} disabled={!canDec}
          onMouseEnter={() => setHoverMinus(true)}
          onMouseLeave={() => setHoverMinus(false)}>−</button>

        <div style={{ width: "1px", height: "22px", backgroundColor: colors.inputBorder, flexShrink: 0 }} />

        <input
          type="number"
          value={value === 0 || value === "" || value === null || value === undefined ? "" : value}
          placeholder="0"
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          inputMode="numeric"
          pattern="[0-9]*"
          className="whr-num-input"
          ref={() => injectPlaceholderStyle()}
          style={{
            width: "46px", height: "42px",
            background: "transparent",
            border: "none", outline: "none",
            color: colors.text,
            caretColor: colors.accent,
            fontSize: "14px", fontWeight: "600",
            textAlign: "center", padding: 0,
            fontFamily: "Outfit",
            MozAppearance: "textfield",
            WebkitAppearance: "none",
            cursor: disabled ? "not-allowed" : "text",
          }}
        />

        <div style={{ width: "1px", height: "22px", backgroundColor: colors.inputBorder, flexShrink: 0 }} />

        <button type="button" style={btnStyle(hoverPlus, canInc)}
          onClick={increment} disabled={!canInc}
          onMouseEnter={() => setHoverPlus(true)}
          onMouseLeave={() => setHoverPlus(false)}>+</button>

        {label && (
          <span style={{
            paddingLeft: "6px", paddingRight: "12px",
            color: colors.subtext, fontSize: "13px",
            fontWeight: "500", whiteSpace: "nowrap",
          }}>{label}</span>
        )}
      </div>
      {isInvalid && <ErrMsg msg={errorMsg} />}
    </div>
  );
};

const RadioRow = ({ value, label, checked, onChange, colors }) => (
  <div onClick={() => onChange(value)} style={{
    display: "flex", alignItems: "center", gap: "12px",
    padding: "13px 16px", borderRadius: "8px",
    border: `1px solid ${checked ? colors.rowActiveBorder : colors.border}`,
    backgroundColor: checked ? colors.rowActive : colors.rowBg,
    cursor: "pointer", marginBottom: "8px", transition: "all 0.2s",
  }}>
    <div style={{
      width: "18px", height: "18px", borderRadius: "50%",
      border: `2px solid ${checked ? colors.accent : colors.inputBorder}`,
      backgroundColor: checked ? colors.accent : "transparent",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, transition: "all 0.2s",
    }}>
      {checked && <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#fff" }} />}
    </div>
    <span style={{ color: colors.text, fontSize: "13px", fontWeight: checked ? "600" : "400" }}>{label}</span>
  </div>
);

const Checkbox = ({ checked, onChange, colors }) => (
  <div onClick={onChange} style={{
    width: "18px", height: "18px", borderRadius: "4px",
    border: `2px solid ${checked ? colors.accent : colors.inputBorder}`,
    backgroundColor: checked ? colors.accent : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexShrink: 0, transition: "all 0.2s",
  }}>
    {checked && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </div>
);

const Toggle = ({ checked, onChange, colors }) => (
  <div onClick={onChange} style={{
    width: "44px", height: "24px", borderRadius: "12px",
    backgroundColor: checked ? colors.accent : colors.border,
    cursor: "pointer", position: "relative", transition: "background 0.2s",
    flexShrink: 0,
  }}>
    <div style={{
      position: "absolute", top: "3px",
      left: checked ? "23px" : "3px",
      width: "18px", height: "18px", borderRadius: "50%",
      backgroundColor: "#fff", transition: "left 0.2s",
      boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
    }} />
  </div>
);

const CriteriaBox = ({ label, children, colors, isInvalid = false }) => (
  <div style={{
    borderRadius: "8px",
    border: `1px solid ${isInvalid ? "#e74c3c" : colors.border}`,
    backgroundColor: colors.rowBg,
    padding: "16px", marginBottom: "8px",
    boxShadow: isInvalid ? "0 0 0 2px rgba(231,76,60,0.15)" : "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  }}>
    <div style={{ color: colors.subtext, fontSize: "12px", marginBottom: "10px" }}>
      {label}
      {isInvalid && (
        <span style={{ color: "#e74c3c", fontSize: "11px", fontWeight: "500", marginLeft: "8px" }}>
          ✕ Required
        </span>
      )}
    </div>
    {children}
  </div>
);

const AbsentRow = ({ label, checkedKey, hoursKey, maxHours = 23, rules, set, colors, submitted }) => {
  const hoursInvalid = submitted && rules[checkedKey] && hoursKey && (!rules[hoursKey] || rules[hoursKey] === 0 || rules[hoursKey] === "");

  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", borderRadius: "8px",
        border: `1px solid ${hoursInvalid ? "#e74c3c" : rules[checkedKey] ? colors.rowActiveBorder : colors.border}`,
        backgroundColor: rules[checkedKey] ? colors.rowActive : colors.rowBg,
        marginBottom: "8px", cursor: "pointer", transition: "all 0.2s",
        boxShadow: hoursInvalid ? "0 0 0 2px rgba(231,76,60,0.15)" : "none",
      }}
      onClick={() => set(checkedKey, !rules[checkedKey])}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Checkbox checked={rules[checkedKey]} onChange={() => set(checkedKey, !rules[checkedKey])} colors={colors} />
        <span style={{ color: colors.text, fontSize: "13px" }}>{label}</span>
      </div>
      {hoursKey && rules[checkedKey] && (
        <div onClick={(e) => e.stopPropagation()}>
          <NumberInputField
            value={rules[hoursKey]}
            onChange={(v) => set(hoursKey, v)}
            label="hrs" min={0} max={maxHours}
            colors={colors}
            required submitted={submitted}
            errorMsg="Enter hours"
          />
        </div>
      )}
    </div>
  );
};

/* =============================================================
   HELPER — reads the first non-empty value from a list of keys
   pick(obj, ["keyA", "key_b", "KEY_C"])
============================================================= */
const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

/* =============================================================
   MAIN COMPONENT
============================================================= */
const WorkingHoursRules = forwardRef(({ theme = "dark", shiftType = "fixed" }, ref) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [submitted,  setSubmitted]  = useState(false);
  const isDark = theme === "dark";

  const [rules, setRules] = useState({
    mandatoryHours:          "",
    mandatoryMinutes:        "",
    presentCriteria:         "gross",
    halfDayEnabled:          false,
    halfDayLessThan:         "",
    halfDayBetweenFrom:      "",
    halfDayBetweenTo:        "",
    fullDayMinHours:         "",
    absentWorkedLessThan:    false,
    absentLessThan:          "",
    absentNoClockIn:         false,
    absentClockInNoOut:      false,
    absentClockInNoOutHours: "",
  });

  const set = (key, val) => setRules((prev) => ({ ...prev, [key]: val }));

  /* ── validate ─────────────────────────────────────────────── */
  const validate = () => {
    setSubmitted(true);
    if (!isExpanded) setIsExpanded(true);
    const errors = [];
    
    if (!rules.mandatoryHours || rules.mandatoryHours === "" || rules.mandatoryHours === 0) 
      errors.push("mandatoryHours");
    
    if (rules.halfDayEnabled) {
      if (!rules.halfDayLessThan || rules.halfDayLessThan === "" || rules.halfDayLessThan === 0) 
        errors.push("halfDayLessThan");
      if (!rules.halfDayBetweenFrom || rules.halfDayBetweenFrom === "" || rules.halfDayBetweenFrom === 0) 
        errors.push("halfDayBetweenFrom");
      if (!rules.halfDayBetweenTo || rules.halfDayBetweenTo === "" || rules.halfDayBetweenTo === 0) 
        errors.push("halfDayBetweenTo");
      if (!rules.fullDayMinHours || rules.fullDayMinHours === "" || rules.fullDayMinHours === 0) 
        errors.push("fullDayMinHours");
    }
    
    if (rules.absentWorkedLessThan && (!rules.absentLessThan || rules.absentLessThan === "" || rules.absentLessThan === 0))
      errors.push("absentLessThan");
    
    if (rules.absentClockInNoOut && (!rules.absentClockInNoOutHours || rules.absentClockInNoOutHours === "" || rules.absentClockInNoOutHours === 0))
      errors.push("absentClockInNoOutHours");
    
    return errors;
  };

  /* ── getData — payload sent to API on save ────────────────── */
  const getData = () => ({
    workHoursPolicy: [{
      mandatoryWorkHours:   rules.mandatoryHours ? String(rules.mandatoryHours) : "",
      mandatoryWorkMinutes: rules.mandatoryMinutes ? String(rules.mandatoryMinutes) : "",
      presentCriteria:      rules.presentCriteria === "gross" ? "1" : "2",
      halfDayCriteria: rules.halfDayEnabled ? [{
        workedLessThanHours: rules.halfDayLessThan ? String(rules.halfDayLessThan) : "",
        workedBetweenFrom:   rules.halfDayBetweenFrom ? String(rules.halfDayBetweenFrom) : "",
        workedBetweenTo:     rules.halfDayBetweenTo ? String(rules.halfDayBetweenTo) : "",
        minimumHourFullDay:  rules.fullDayMinHours ? String(rules.fullDayMinHours) : "",
      }] : [],
      absentCriteria: [{
        workedLessThanHours:                rules.absentWorkedLessThan && rules.absentLessThan ? String(rules.absentLessThan) : "",
        noClockInOrAbsent:                  rules.absentNoClockIn ? "1" : "0",
        onlyClockInWithoutClockOutAfterHrs: rules.absentClockInNoOut && rules.absentClockInNoOutHours ? String(rules.absentClockInNoOutHours) : "",
      }],
    }],
  });

  /* ── setData — called from parent handleEdit ──────────────────
   *
   * Handles THREE shapes automatically:
   *
   * Shape 1 — workHoursPolicy array (internal / round-trip):
   *   { workHoursPolicy: [{ mandatoryWorkHours, halfDayCriteria[], absentCriteria[] }] }
   *
   * Shape 2 — flat camelCase from row.details[0]  ← most common from your API
   *   { mandatoryWorkHours, mandatoryWorkMinutes, presentCriteria,
   *     workedLessThanHours, workedBetweenFrom, workedBetweenTo,
   *     minimumHourFullDay, noClockInOrAbsent,
   *     onlyClockInWithoutClockOutAfterHrs }
   *
   * Shape 3 — flat snake_case / prefixed  (whp_*, mandatory_work_hours …)
   *
   * ⚠️  Put YOUR actual API key names FIRST in each pick() array.
   * ─────────────────────────────────────────────────────────── */
  const setData = (apiData) => {
    if (!apiData?.workHoursPolicy?.[0]) return;

    const policy  = apiData.workHoursPolicy[0];
    const halfDay = policy.halfDayCriteria?.[0]  || {};
    const absent  = policy.absentCriteria?.[0]   || {};

    // Half-day is active only when the API had values
    const halfDayEnabled = !!(
      halfDay.workedBetweenFrom ||
      halfDay.workedBetweenTo   ||
      halfDay.minimumHourFullDay
    );

    setRules({
      // Mandatory hours
      mandatoryHours:   policy.mandatoryWorkHours ? parseInt(policy.mandatoryWorkHours) : "",
      mandatoryMinutes: policy.mandatoryWorkMinutes ? parseInt(policy.mandatoryWorkMinutes) : "",

      // Present criteria: "1" → gross, "2" → mandatory
      presentCriteria: policy.presentCriteria === "2" ? "mandatory" : "gross",

      // Half-day
      halfDayEnabled,
      halfDayLessThan:    halfDay.workedLessThanHours ? parseInt(halfDay.workedLessThanHours) : "",
      halfDayBetweenFrom: halfDay.workedBetweenFrom ? parseInt(halfDay.workedBetweenFrom) : "",
      halfDayBetweenTo:   halfDay.workedBetweenTo ? parseInt(halfDay.workedBetweenTo) : "",
      fullDayMinHours:    halfDay.minimumHourFullDay ? parseInt(halfDay.minimumHourFullDay) : "",

      // Absent criteria
      absentWorkedLessThan:    !!(absent.workedLessThanHours && absent.workedLessThanHours !== ""),
      absentLessThan:          absent.workedLessThanHours ? parseInt(absent.workedLessThanHours) : "",
      absentNoClockIn:         absent.noClockInOrAbsent === "1",
      absentClockInNoOut:      !!(absent.onlyClockInWithoutClockOutAfterHrs && absent.onlyClockInWithoutClockOutAfterHrs !== ""),
      absentClockInNoOutHours: absent.onlyClockInWithoutClockOutAfterHrs ? parseInt(absent.onlyClockInWithoutClockOutAfterHrs) : "",
    });
  };

  useImperativeHandle(ref, () => ({ validate, getData, setData }));

  /* ── colours ──────────────────────────────────────────────── */
  const colors = {
    cardBg:          isDark ? "#13132b" : "#ffffff",
    sectionBg:       isDark ? "#1a1a35" : "#f0ecf8",
    rowBg:           isDark ? "#1c1c34" : "#f5f2fc",
    rowActive:       isDark ? "rgba(108,92,231,0.18)" : "rgba(108,92,231,0.08)",
    rowActiveBorder: "#6c5ce7",
    border:          isDark ? "rgba(108,92,231,0.18)" : "rgba(108,92,231,0.18)",
    text:            isDark ? "#ffffff" : "#1a0f2e",
    subtext:         isDark ? "rgba(255, 255, 255, 0.82)" : "rgba(74,42,130,0.6)",
    accent:          "#6c5ce7",
    accentLight:     isDark ? "rgba(108,92,231,0.25)" : "rgba(108,92,231,0.12)",
    inputBg:         isDark ? "#2a1a3500" : "#ffffff",
    inputBorder:     isDark ? "rgba(157, 92, 231, 0.25)" : "rgba(108,92,231,0.2)",
  };

  const sectionLabelStyle = {
    color: colors.text, fontSize: "13px", fontWeight: "600",
    marginBottom: "12px", display: "block", letterSpacing: "0.01em",
  };

  const betweenRangeInvalid = submitted && rules.halfDayEnabled &&
    rules.halfDayBetweenFrom && rules.halfDayBetweenFrom > 0 && 
    rules.halfDayBetweenTo && rules.halfDayBetweenTo > 0 &&
    Number(rules.halfDayBetweenFrom) >= Number(rules.halfDayBetweenTo);

  const presentCriteriaInvalid = submitted && !rules.presentCriteria;

  /* ── render ───────────────────────────────────────────────── */
  return (
    <div style={{
      backgroundColor: colors.cardBg, borderRadius: "12px",
      overflow: "hidden", border: `1px solid ${colors.border}`,
      fontFamily: "'Outfit', system-ui, sans-serif",
    }}>
      {/* ── Header ── */}
      <div onClick={() => setIsExpanded(!isExpanded)} style={{
        padding: "18px 24px", backgroundColor: colors.sectionBg,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: isExpanded ? `1px solid ${colors.border}` : "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          {/* <div style={{
            width: "36px", height: "36px", borderRadius: "10px",
            backgroundColor: colors.accent,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
          }}></div> */}
          <div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: colors.text }}>
              Working Hours Rules
            </div>
            <div style={{ fontSize: "12px", color: colors.subtext, marginTop: "2px" }}>
              Define attendance marking criteria
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{
            width: "28px", height: "28px", borderRadius: "6px",
            border: `1px solid ${colors.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect x="1" y="1" width="4" height="4" rx="1" fill={colors.subtext}/>
              <rect x="7" y="1" width="4" height="4" rx="1" fill={colors.subtext}/>
              <rect x="1" y="7" width="4" height="4" rx="1" fill={colors.subtext}/>
              <rect x="7" y="7" width="4" height="4" rx="1" fill={colors.subtext}/>
            </svg>
          </div>
          <div style={{
            width: "28px", height: "28px", borderRadius: "6px",
            border: `1px solid ${colors.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: colors.subtext, fontSize: "14px",
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}>∨</div>
        </div>
      </div>

      {/* ── Body ── */}
      {isExpanded && (
        <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "28px" }}>

          {/* 1 — Mandatory Working Hours */}
          <div>
            <span style={sectionLabelStyle}>
              Mandatory Working Hours
              <span style={{ color: "#e74c3c", marginLeft: "3px" }}>*</span>
            </span>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-start" }}>
              <NumberInputField
                value={rules.mandatoryHours}
                onChange={(v) => set("mandatoryHours", v)}
                label="hrs" min={0} max={23}
                colors={colors} required submitted={submitted} errorMsg="Enter hours"
              />
              <NumberInputField
                value={rules.mandatoryMinutes}
                onChange={(v) => set("mandatoryMinutes", v)}
                label="min" min={0} max={59}
                colors={colors} errorMsg="Enter minutes"
              />
            </div>
            <div style={{ color: colors.subtext, fontSize: "12px", marginTop: "8px" }}>
              Minimum hours required to mark Present
            </div>
          </div>

          {/* 2 — Present Criteria */}
          <div>
            <span style={sectionLabelStyle}>
              Present Criteria
              <span style={{ color: "#e74c3c", marginLeft: "3px" }}>*</span>
            </span>
            {[
              { value: "gross",     label: "Based on Gross Hours"     },
              { value: "mandatory", label: "Based on Mandatory Hours" },
            ].map((opt) => (
              <RadioRow key={opt.value} value={opt.value} label={opt.label}
                checked={rules.presentCriteria === opt.value}
                onChange={(val) => set("presentCriteria", val)}
                colors={colors}
              />
            ))}
            {presentCriteriaInvalid && <ErrMsg msg="Please select a present criteria" />}
          </div>

          {/* 3 — Half Day Criteria */}
          <div>
            <div style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between", marginBottom: "12px",
            }}>
              <span style={{ ...sectionLabelStyle, marginBottom: 0 }}>
                Half Day Criteria
                {rules.halfDayEnabled && <span style={{ color: "#e74c3c", marginLeft: "3px" }}>*</span>}
              </span>
              <Toggle
                checked={rules.halfDayEnabled}
                onChange={() => set("halfDayEnabled", !rules.halfDayEnabled)}
                colors={colors}
              />
            </div>

            {rules.halfDayEnabled && (
              <>
                <CriteriaBox
                  label="If worked less than"
                  colors={colors}
                  isInvalid={submitted && (!rules.halfDayLessThan || rules.halfDayLessThan === "" || rules.halfDayLessThan === 0)}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
                    <NumberInputField
                      value={rules.halfDayLessThan}
                      onChange={(v) => set("halfDayLessThan", v)}
                      label="hrs" min={0} max={23}
                      colors={colors} required submitted={submitted} errorMsg="Required"
                    />
                    <span style={{ color: colors.subtext, fontSize: "13px", whiteSpace: "nowrap", lineHeight: "42px" }}>
                      → Mark Half Day
                    </span>
                  </div>
                </CriteriaBox>

                <CriteriaBox
                  label="If worked between"
                  colors={colors}
                  isInvalid={
                    submitted && (
                      (!rules.halfDayBetweenFrom || rules.halfDayBetweenFrom === "" || rules.halfDayBetweenFrom === 0) ||
                      (!rules.halfDayBetweenTo || rules.halfDayBetweenTo === "" || rules.halfDayBetweenTo === 0) ||
                      betweenRangeInvalid
                    )
                  }
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", flexWrap: "wrap" }}>
                    <NumberInputField
                      value={rules.halfDayBetweenFrom}
                      onChange={(v) => set("halfDayBetweenFrom", v)}
                      label="hrs" min={0} max={23}
                      colors={colors} required submitted={submitted} errorMsg="Required"
                    />
                    <span style={{ color: colors.subtext, fontSize: "13px", lineHeight: "42px" }}>and</span>
                    <NumberInputField
                      value={rules.halfDayBetweenTo}
                      onChange={(v) => set("halfDayBetweenTo", v)}
                      label="hrs" min={0} max={23}
                      colors={colors} required submitted={submitted} errorMsg="Required"
                    />
                    <span style={{ color: colors.subtext, fontSize: "13px", whiteSpace: "nowrap", lineHeight: "42px" }}>
                      → Half Day
                    </span>
                  </div>
                  {betweenRangeInvalid && <ErrMsg msg="'From' must be less than 'To'" />}
                </CriteriaBox>

                <CriteriaBox
                  label="Full Day Minimum Hours"
                  colors={colors}
                  isInvalid={submitted && (!rules.fullDayMinHours || rules.fullDayMinHours === "" || rules.fullDayMinHours === 0)}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
                    <NumberInputField
                      value={rules.fullDayMinHours}
                      onChange={(v) => set("fullDayMinHours", v)}
                      label="hrs" min={0} max={23}
                      colors={colors} required submitted={submitted} errorMsg="Required"
                    />
                    <span style={{ color: colors.subtext, fontSize: "13px", whiteSpace: "nowrap", lineHeight: "42px" }}>
                      → Mark Full Day
                    </span>
                  </div>
                </CriteriaBox>
              </>
            )}
          </div>

          {/* 4 — Absent Criteria */}
          <div>
            <span style={sectionLabelStyle}>Absent Criteria</span>

            {/* worked less than X hrs → absent */}
            <AbsentRow
              label="Mark Absent if worked less than"
              checkedKey="absentWorkedLessThan"
              hoursKey="absentLessThan"
              rules={rules} set={set} colors={colors} submitted={submitted}
            />

            {/* no clock-in → absent */}
            {/* <AbsentRow
              label="No Clock-In → Mark Absent"
              checkedKey="absentNoClockIn"
              hoursKey={null}
              rules={rules} set={set} colors={colors} submitted={submitted}
            /> */}

            {/* clocked-in but no clock-out after X hrs → absent */}
            {/* <AbsentRow
              label="Clocked-In but no Clock-Out after"
              checkedKey="absentClockInNoOut"
              hoursKey="absentClockInNoOutHours"
              rules={rules} set={set} colors={colors} submitted={submitted}
            /> */}
          </div>

        </div>
      )}
    </div>
  );
});

export default WorkingHoursRules;